import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom"
import AuthService from "../../AuthService";
import { useSnackbar } from "notistack";
import {
  Grid,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Util from "../../utils";
import TituloPrincipal from "../Generico/TituloPrincipal";
import Template from "../Generico/Template";
import md5 from "js-md5";
import { extractErrorMessage } from "../../utils/HttpUtils";

const Usuario = () => {
  const { id } = useParams()
  const [usuario, setUsuario] = useState({
    nome: "",
    token: "",
    username: "",
    email: "",
    password: "",
    ativo: true,
    grupoPermissao: { id: "" },
    empresa: { id: "" },
  });
  const [gruposPermissao, setGruposPermissao] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const textArea = useRef();
  const [empresaInput, setEmpresaInput] = useState("");
  const isLoggedUser = useMemo(() => {
    if (!id) return false

    const service = new AuthService()
    const profile = service.getProfile()

    return profile.id === Number(id)
  }, [id])

  useEffect(() => {
    if (id) {
      carregarUsuario(id)
    } else {
      const service = new AuthService()
      const profile = service.getProfile()
      carregarUsuario(profile.id)
    }

    carregarSelects()

    //eslint-disable-next-line
  }, [id])

  const handleChange = (event) => {
    const novoUsuario = { ...usuario };
    Util.setValue(novoUsuario, event.target.name, event.target.value);
    setUsuario(novoUsuario);
  };

  const carregarUsuario = (idUsuario) => {
    const service = new AuthService();

    if (!service.getProfile().admin) {
      enqueueSnackbar(
        "Seu usuário não tem permissão para editar outros usuários",
        {
          variant: "error",
        }
      );
      return;
    }

    service
      .get(`/usuario/${idUsuario}`)
      .then((retorno) => {
        retorno.passwordOriginal = retorno.password;
        retorno.empresaOriginal = retorno.empresa;
        setUsuario(retorno);
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(`Falha ao buscar usuario com ID ${idUsuario}`, {
          variant: "error",
        });
      });
  };
  const handleError = (message) => (err) => {
    console.error(err);
    enqueueSnackbar(message, { variant: "error" });
  };

  const carregarSelects = () => {
    const service = new AuthService();

    service
      .get("/grupoPermissao/list")
      .then(setGruposPermissao)
      .catch(handleError("Falha ao buscar grupo de permissões"));

    service
      .get("/empresa/list")
      .then(setEmpresas)
      .catch(handleError("Falha ao buscar empresas"));
  };

  const salvarUsuario = () => {
    if (!usuario.id) {
      enqueueSnackbar("Nenhum usuário para ser salvo", { variant: "error" });
      return;
    }

    if (!usuario.empresa) {
      enqueueSnackbar("Nenhuma empresa selecionada", { variant: "error" });
      return;
    }

    const usuarioParaSalvar = { ...usuario };
    if (usuarioParaSalvar.passwordOriginal !== usuarioParaSalvar.password) {
      usuarioParaSalvar.password = md5(usuarioParaSalvar.password);
    }

    const mudouEmpresaLogada = usuarioParaSalvar.empresaOriginal?.id !== usuarioParaSalvar.empresa?.id;

    const service = new AuthService();
    service
      .post("/usuario", usuarioParaSalvar)
      .then((retorno) => {
        setUsuario(retorno);
        enqueueSnackbar("Usuário salvo com sucesso", { variant: "success" });

        if (mudouEmpresaLogada) {
          let profile = JSON.parse(localStorage.getItem("_profile"));
          service.get(`/empresa/${usuarioParaSalvar.empresa.id}`).then((retorno) => {
            profile.empresa = retorno;
            localStorage.setItem("_profile", JSON.stringify(profile));
            window.location.reload();
          });
        }
      }).catch((err) => {
        console.log(err)

        extractErrorMessage(err, 'Houve um erro ao salvar o usuário').then((msg) =>
          enqueueSnackbar('Houve um erro ao salvar o usuário - ' + msg, { variant: "error" })
        )
      })
  };

  const handleAtualizarToken = () => {
    const service = new AuthService();
    service
      .post("/usuario/atualizarToken", usuario)
      .then((resp) => {
        setUsuario({ ...usuario, token: resp.token });
      })
      .catch(console.error);
  };

  return (
    <Template documentTitle="Dados do usuário">
      <TituloPrincipal
        title="Dados do usuário"
        subTitle="Alterar os dados do usuário"
      />
      <Paper sx={{ marginTop: "1rem" }}>
        <Box p={4}>
          <form noValidate autoComplete="off">
            <Grid container spacing={3} alignItems="center">
              <Grid size={12}>
                <TextField
                  id="nome"
                  label="Nome do usuário"
                  variant="outlined"
                  name="nome"
                  fullWidth
                  value={usuario.nome || ""}
                  onChange={handleChange}
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  id="username"
                  InputProps={{ readOnly: true }}
                  label="Login"
                  variant="outlined"
                  name="username"
                  fullWidth
                  value={usuario.username || ""}
                  helperText="Não é possível alterar o login"
                  autoComplete="username"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  id="email"
                  label="E-mail"
                  type="email"
                  onChange={handleChange}
                  variant="outlined"
                  name="email"
                  fullWidth
                  value={usuario.email || ""}
                  autoComplete="email"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  id="password"
                  label="Senha"
                  type="password"
                  onChange={handleChange}
                  variant="outlined"
                  name="password"
                  fullWidth
                  value={usuario.password || ""}
                  autoComplete="current-password"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  id="grupoPermissao"
                  label="Grupo de permissão"
                  variant="outlined"
                  name="grupoPermissao.id"
                  fullWidth
                  value={usuario.grupoPermissao?.id || ""}
                  select
                  onChange={handleChange}
                >
                  <MenuItem value={"-1"}>nenhum grupo</MenuItem>
                  {gruposPermissao.map((grupo) => (
                    <MenuItem key={grupo.id} value={grupo.id}>
                      {grupo.descricao}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={12}>
                {usuario.admin && (
                  <Autocomplete
                    options={empresas}
                    getOptionLabel={(option) => option?.nome || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={usuario.empresa}
                    inputValue={empresaInput}
                    onInputChange={(event, newInputValue) => {
                      setEmpresaInput(newInputValue);
                    }}
                    onChange={(__, newValue) => {
                      setUsuario({ ...usuario, empresa: newValue || null });
                      setEmpresaInput(newValue?.nome || "");
                    }}
                    filterOptions={(options, state) => {
                      return options.filter((option) =>
                        option.nome
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empresa logada"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                    loading={empresas.length === 0}
                    loadingText="Carregando..."
                    disableClearable
                  />
                )}
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Token do Usuario"
                  variant="outlined"
                  fullWidth
                  inputRef={textArea}
                  value={usuario.token || ""}
                  disabled={!usuario.admin}
                />
              </Grid>

              <Grid size={12}>
                <Box display="flex" flexDirection="row">
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: "8rem" }}
                    onClick={salvarUsuario}
                  >
                    Salvar
                  </Button>
                  <Box pl={2}>
                    <Button
                      disabled={isLoggedUser}
                      variant="contained"
                      color="secondary"
                      startIcon={<RefreshIcon />}
                      onClick={handleAtualizarToken}
                    >
                      Atualizar token
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
    </Template>
  );
};

export default Usuario;
